import { useState } from "react";
import { format, parseISO } from "date-fns";
import { ChevronDown, ChevronRight, Undo2, Bot, Hand } from "lucide-react";

const fmt = d => { try { return format(parseISO(d), "EEE d MMM yyyy · HH:mm"); } catch { return d; } };
const fmtD = d => { try { return format(parseISO(d), "d MMM"); } catch { return d || "TBC"; } };
const KIND = { new: ["New", "text-[#83B68B] border-[#83B68B]/50"], changed: ["Moved", "text-amber-300 border-amber-400/50"], cancelled: ["Removed", "text-red-300 border-red-400/50"] };

export function FARunsList({ runs, isAdmin, onRevert }) {
  const [open, setOpen] = useState(null);
  if (!runs.length) return <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="fa-runs-empty">No sync runs recorded yet — runs appear after the next overnight pull or a manual "Import now".</div>;
  return (
    <div className="space-y-2" data-testid="fa-runs-list">
      {runs.map(run => {
        const isOpen = open === run.run_id;
        const live = run.entries.filter(e => !e.reverted);
        return (
          <div key={run.run_id} className={`card-tactical rounded-md ${run.reverted_at ? "opacity-60" : ""}`} data-testid={`fa-run-${run.run_id}`}>
            <button type="button" onClick={() => setOpen(isOpen ? null : run.run_id)} className="w-full text-left p-4 flex flex-wrap items-center gap-3" data-testid={`fa-run-toggle-${run.run_id}`}>
              {isOpen ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
              {run.source === "cron" ? <Bot className="w-4 h-4 text-[#83B68B]" /> : <Hand className="w-4 h-4 opacity-70" />}
              <span className="font-display font-bold">{fmt(run.at)}</span>
              <span className="text-xs opacity-60 uppercase tracking-widest">{run.source === "cron" ? "Overnight" : "Manual"} · {[...new Set(run.leagues)].join(", ")}</span>
              <span className="ml-auto text-xs flex gap-3">
                <span className="text-[#83B68B]">{run.counts.new} new</span><span className="text-amber-300">{run.counts.changed} moved</span><span className="text-red-300">{run.counts.cancelled} removed</span>
                {run.reverted_at && <span className="opacity-70">· reverted</span>}
              </span>
            </button>
            {isOpen && (
              <div className="border-t border-border p-4 space-y-2">
                {run.entries.length === 0 && <div className="text-xs opacity-60">No changes in this run.</div>}
                {run.entries.map(e => {
                  const k = KIND[e.kind] || [e.kind, "border-border"];
                  return (
                    <div key={e.fa_id} className={`flex flex-wrap items-center gap-3 text-sm p-2 rounded border border-border/60 ${e.reverted ? "opacity-40 line-through" : ""}`} data-testid={`fa-run-entry-${run.run_id}-${e.fa_id}`}>
                      <span className={`text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border ${k[1]}`}>{k[0]}</span>
                      <span className="opacity-60 text-xs">{e.league_name}{e.matched_team_name ? ` · ${e.matched_team_name}` : ""}</span>
                      <span className="font-semibold">{e.home_team} v {e.away_team}</span>
                      <span className="text-xs opacity-80">
                        {e.kind === "changed" && e.before ? `${fmtD(e.before.date)} ${e.before.kickoff} → ${fmtD(e.after.date)} ${e.after.kickoff}` : e.kind === "new" ? `${fmtD(e.after?.date)} ${e.after?.kickoff || ""}` : `was ${fmtD(e.before?.date)} ${e.before?.kickoff || ""}`}
                      </span>
                      {e.flagged_pending && !e.reverted && <span className="text-[10px] text-amber-300">coach asked to confirm</span>}
                      {isAdmin && !e.reverted && (
                        <button type="button" onClick={() => onRevert(run.run_id, [e.fa_id])} data-testid={`fa-run-revert-entry-${e.fa_id}`} className="ml-auto text-[10px] tracking-widest uppercase inline-flex items-center gap-1 opacity-70 hover:opacity-100 hover:text-red-300"><Undo2 className="w-3 h-3" /> Revert</button>
                      )}
                    </div>
                  );
                })}
                {isAdmin && live.length > 0 && (
                  <div className="flex justify-end pt-1">
                    <button type="button" onClick={() => { if (window.confirm(`Revert all ${live.length} remaining entries from this run?`)) onRevert(run.run_id, null); }} data-testid={`fa-run-revert-all-${run.run_id}`} className="px-4 py-2 rounded border border-red-400/50 text-red-300 text-[10px] tracking-widest uppercase inline-flex items-center gap-1 hover:bg-red-500/10"><Undo2 className="w-3 h-3" /> Revert whole run</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
